import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { FileDown, RefreshCw, X } from 'lucide-react';
import { Button, Modal, ModalTitle, Overlay } from './ui';
import ProgramSectionView from '../program/ProgramSectionView';
import ProgramPrint from '../program/ProgramPrint';
import { CoverPages, PageUrlContext } from '../program/ProgramPages';
import { fetchGeneratedPdfAvailable, fetchPdfPageImage, fetchProgramPdfPreview } from './ProgramAdminApi';

// The admin's preview of the public program (decision 7 of plan §7.6, §7.7 item 9): the answer of
// publicapi.ProgramPreviewController is exactly the public shape, so it renders with the same
// component the public page uses (ProgramSectionView). Only the "is it published" gate is skipped.
// Two views: the website as the public sees it, and the PDF as the server makes it (German or English,
// GeneratedProgramPdf.preview, also for a draft) shown in the browser's PDF viewer - offered when the
// server has a PDF renderer. "Export as PDF" prints the same program in its print mode (ProgramPrint):
// the browser's print dialog saves it as a PDF. The website view also shows the title pages (on the
// event page in place of its main image) and the sponsor pages where the admin placed them; their
// images come through the admin route (with the login), so also a draft's.

const segment = (active) => ({
  border: '1px solid #cbd5e1',
  background: active ? '#0f172a' : '#fff',
  color: active ? '#fff' : '#0f172a',
  padding: '.4rem .8rem',
  fontWeight: 600,
  fontSize: '.85rem',
  cursor: 'pointer',
});

// The text of an error answer that came back as a Blob (the PDF call asks for a Blob).
const errorText = async (err, fallback) => {
  const data = err?.response?.data;
  if (typeof data === 'string' && data) return data;
  if (data && typeof data.text === 'function') {
    try {
      const text = await data.text();
      if (text) return text;
    } catch (e) { /* the fallback below */ }
  }
  return fallback;
};

// The title and sponsor page images of the previewed program as object URLs, and the resolver
// ProgramPages reads them through (nothing for an image not loaded yet).
const usePageImages = (eventId, program) => {
  const [urls, setUrls] = useState({});
  const pages = program?.pages;
  const wanted = [...(pages?.cover || []), ...(pages?.sponsors || [])].map((p) => `${p.pageId}-${p.index}`).join(',');

  useEffect(() => {
    if (eventId == null || !wanted) return undefined;
    let live = true;
    const made = [];
    Promise.all(wanted.split(',').map((key) => {
      const [pageId, index] = key.split('-');
      return fetchPdfPageImage(eventId, pageId, index)
        .then((blob) => { const url = URL.createObjectURL(blob); made.push(url); return [key, url]; })
        .catch(() => [key, null]);
    })).then((pairs) => { if (live) setUrls(Object.fromEntries(pairs)); });
    return () => {
      live = false;
      made.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [eventId, wanted]);

  return useCallback((pageId, index) => urls[`${pageId}-${index}`] || null, [urls]);
};

const PdfView = ({ eventId, language, available }) => {
  const intl = useIntl();
  const [pdf, setPdf] = useState({}); // language -> { url } | { loading } | { error }
  const urls = useRef([]);

  const load = useCallback(async (lang) => {
    setPdf((p) => ({ ...p, [lang]: { loading: true } }));
    try {
      const blob = await fetchProgramPdfPreview(eventId, lang);
      const url = URL.createObjectURL(blob);
      urls.current.push(url);
      setPdf((p) => ({ ...p, [lang]: { url } }));
    } catch (err) {
      const text = await errorText(err, intl.formatMessage({ id: 'programAdmin.preview.pdfFailed', defaultMessage: 'The PDF preview could not be made.' }));
      setPdf((p) => ({ ...p, [lang]: { error: text } }));
    }
  }, [eventId, intl]);

  useEffect(() => {
    if (available && !pdf[language]) load(language);
  }, [available, language, pdf, load]);

  useEffect(() => () => urls.current.forEach((u) => URL.revokeObjectURL(u)), []);

  if (!available) {
    return (
      <p style={{ padding: '1rem 1.25rem', color: '#64748b' }}>
        <FormattedMessage
          id="programAdmin.preview.pdfUnavailable"
          defaultMessage="The PDF preview needs the PDF service on the server. “Export as PDF” works without it."
        />
      </p>
    );
  }
  const current = pdf[language] || { loading: true };
  return (
    <div style={{ padding: '1rem 1.25rem' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '.5rem' }}>
        <Button type="button" secondary disabled={current.loading} onClick={() => load(language)}>
          <RefreshCw size={15} aria-hidden="true" />
          <FormattedMessage id="programAdmin.preview.pdfReload" defaultMessage="Make it again" />
        </Button>
      </div>
      {current.loading && (
        <p style={{ color: '#64748b' }}>
          <FormattedMessage id="programAdmin.preview.pdfLoading" defaultMessage="The server is making the PDF… (a few seconds)" />
        </p>
      )}
      {current.error && <p role="alert" style={{ padding: '.6rem .7rem', background: '#fee2e2', color: '#991b1b' }}>{current.error}</p>}
      {current.url && (
        <iframe
          title={intl.formatMessage({ id: 'programAdmin.preview.pdfTitle', defaultMessage: 'PDF preview' })}
          src={current.url}
          style={{ width: '100%', height: 'calc(100vh - 13rem)', border: '1px solid #e2e8f0', background: '#f8fafc' }}
        />
      )}
    </div>
  );
};

const PublicPreviewModal = ({ open, loading, error, program, people, language, eventId, eventTitle, onClose }) => {
  const intl = useIntl();
  const [printing, setPrinting] = useState(false);
  const [view, setView] = useState('web'); // 'web' | 'pdf'
  const [pdfLanguage, setPdfLanguage] = useState(language === 'en' ? 'en' : 'de');
  const [available, setAvailable] = useState(false);
  const donePrinting = useCallback(() => setPrinting(false), []);
  const pageUrl = usePageImages(open ? eventId : null, program);

  useEffect(() => {
    if (!open || eventId == null) return undefined;
    let live = true;
    setView('web');
    setPdfLanguage(language === 'en' ? 'en' : 'de');
    fetchGeneratedPdfAvailable(eventId)
      .then((a) => { if (live) setAvailable(!!a); })
      .catch(() => { if (live) setAvailable(false); });
    return () => { live = false; };
  }, [open, eventId, language]);

  if (!open) return null;
  return (
    <Overlay onClick={onClose}>
      <Modal
        role="dialog"
        aria-label={intl.formatMessage({ id: 'programAdmin.preview.title', defaultMessage: 'The program as the public sees it' })}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '76rem', width: '100%', maxHeight: 'calc(100vh - 2rem)', overflowY: 'auto', padding: 0 }}
      >
        <div
          style={{
            position: 'sticky', top: 0, zIndex: 40, display: 'flex', flexWrap: 'wrap', alignItems: 'center',
            justifyContent: 'space-between', gap: '0.75rem 1rem', padding: '1rem 1.25rem', background: '#fff',
            borderBottom: '1px solid #e2e8f0',
          }}
        >
          <ModalTitle style={{ margin: 0 }}>
            <FormattedMessage id="programAdmin.preview.title" defaultMessage="The program as the public sees it" />
          </ModalTitle>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' }}>
            <div role="group" aria-label={intl.formatMessage({ id: 'programAdmin.preview.view', defaultMessage: 'View' })} style={{ display: 'flex' }}>
              <button type="button" aria-pressed={view === 'web'} style={segment(view === 'web')} onClick={() => setView('web')}>
                <FormattedMessage id="programAdmin.preview.viewWeb" defaultMessage="Website" />
              </button>
              <button type="button" aria-pressed={view === 'pdf'} style={{ ...segment(view === 'pdf'), borderLeft: 0 }} onClick={() => setView('pdf')}>
                <FormattedMessage id="programAdmin.preview.viewPdf" defaultMessage="PDF" />
              </button>
            </div>
            {view === 'pdf' && available && (
              <div role="group" aria-label={intl.formatMessage({ id: 'programAdmin.preview.pdfLanguage', defaultMessage: 'PDF language' })} style={{ display: 'flex' }}>
                {['de', 'en'].map((l, i) => (
                  <button
                    key={l}
                    type="button"
                    aria-pressed={pdfLanguage === l}
                    style={{ ...segment(pdfLanguage === l), borderLeft: i ? 0 : undefined }}
                    onClick={() => setPdfLanguage(l)}
                  >
                    {l.toUpperCase()}
                  </button>
                ))}
              </div>
            )}
            <Button type="button" onClick={() => setPrinting(true)} disabled={!program || printing}>
              <FileDown size={16} aria-hidden="true" />
              <FormattedMessage id="programAdmin.preview.pdf" defaultMessage="Export as PDF" />
            </Button>
            <button
              type="button"
              onClick={onClose}
              aria-label={intl.formatMessage({ id: 'programAdmin.preview.close', defaultMessage: 'Close' })}
              style={{ border: 0, background: 'none', cursor: 'pointer', color: '#64748b', padding: '0.4rem' }}
            >
              <X size={20} />
            </button>
          </div>
        </div>
        {view === 'web' && program && (
          <p style={{ margin: 0, padding: '0.6rem 1.25rem', background: '#f8fafc', color: '#64748b', fontSize: '.85rem' }}>
            <FormattedMessage
              id="programAdmin.preview.pdfHint"
              defaultMessage="The PDF opens in the browser's print dialog: choose “Save as PDF” as the printer."
            />
          </p>
        )}
        {loading && (
          <p style={{ color: '#64748b', padding: '1rem 1.25rem' }}>
            <FormattedMessage id="programAdmin.load.loading" defaultMessage="Loading the program…" />
          </p>
        )}
        {error && <p role="alert" style={{ margin: '1rem 1.25rem', padding: '.6rem .7rem', background: '#fee2e2', color: '#991b1b' }}>{error}</p>}
        {view === 'web' && program && (
          <PageUrlContext.Provider value={pageUrl}>
            {program.pages?.cover?.length > 0 && (
              <div style={{ maxWidth: '36rem', margin: '0 auto', padding: '1.25rem 1.25rem 0' }}>
                <p style={{ margin: '0 0 .6rem', color: '#64748b', fontSize: '.85rem', textAlign: 'center' }}>
                  <FormattedMessage
                    id="programAdmin.preview.coverHint"
                    defaultMessage="Title page: on the event page in place of the event image, and in front of the PDF."
                  />
                </p>
                <CoverPages program={program} alt={eventTitle || ''} />
              </div>
            )}
            <ProgramSectionView program={program} people={people} language={language} />
          </PageUrlContext.Provider>
        )}
        {view === 'pdf' && <PdfView eventId={eventId} language={pdfLanguage} available={available} />}
      </Modal>
      {printing && program && (
        <PageUrlContext.Provider value={pageUrl}>
          <ProgramPrint program={program} people={people} language={language} title={eventTitle} onDone={donePrinting} />
        </PageUrlContext.Provider>
      )}
    </Overlay>
  );
};

export default PublicPreviewModal;
